/**
 * Per-camera motion setting (ENG-2242, backend ENG-2067 / admin v2.54.0).
 *
 * `GET/PATCH camera/{id}/general_info/` carries:
 * - `use_motion_override` (read/write, normal camera-edit permission):
 *   `null` = inherit the site, `true` = use motion, `false` = always stream.
 * - `use_motion` (read-only): the resolved value. Precedence is
 *   override > Milestone's per-camera `milestone_motion` > the site's
 *   `Customer.use_motion`.
 *
 * Every PATCH marks the site undeployed server-side, and the value reaches the
 * connector through the settings file, so callers must `markSiteUnsynced()`
 * and tell the operator to Sync.
 */
import { create } from "zustand";
import { createApi } from "../../utils/createApi";
import { readsSettingsFile } from "../Sites/SiteAboutPage/SiteAboutPageUtils/readsSettingsFile";

export const MOTION_OVERRIDE_FIELD = "use_motion_override";

export type MotionOverride = boolean | null;
export type MotionChoice = "inherit" | "motion" | "stream";

export const MOTION_CHOICES: MotionChoice[] = ["inherit", "motion", "stream"];

export const MOTION_SETTING_LABEL = "Motion";

export const MOTION_CHOICE_LABELS: Record<
  Exclude<MotionChoice, "inherit">,
  string
> = {
  motion: "Use motion",
  stream: "Always stream",
};

// Jacob's rule for motion copy: state the trade-off, pros AND cons.
export const MOTION_CHOICE_DESCRIPTIONS: Record<MotionChoice, string> = {
  inherit: "Follow the site's motion setting.",
  motion:
    "Use motion: we pull this camera only while your VMS or camera reports motion. Lower load, but if the camera's motion detection isn't set up or never fires, we won't see anything until it does. For Genetec, a camera with no motion events waits for motion and is not pulled until it raises one. Choose Always stream for cameras whose motion detection isn't configured.",
  stream:
    "Always stream: we pull this camera continuously. Most reliable detection, but more bandwidth and processing for this camera.",
};

export const MOTION_SAVED_NOTE =
  "Saved. Sync the site to apply. If the site is armed, accept the restart when you sync — re-arming alone won't apply it.";
export const MOTION_SAVE_FAILED =
  "Couldn't save the motion setting. Try again.";

export const onOff = (value: boolean) => (value ? "On" : "Off");

/** "Inherit site setting (currently: On)"; no suffix while unknown. */
export const inheritLabel = (inherited: boolean | null | undefined) =>
  typeof inherited === "boolean"
    ? `Inherit site setting (currently: ${onOff(inherited)})`
    : "Inherit site setting";

export const overrideToChoice = (override: unknown): MotionChoice =>
  override === true ? "motion" : override === false ? "stream" : "inherit";

export const choiceToOverride = (choice: MotionChoice): MotionOverride =>
  choice === "motion" ? true : choice === "stream" ? false : null;

/**
 * Integrations where a per-camera motion setting means nothing, so the control
 * is not offered:
 * - SMTP integrations: the emailed frame is the signal and admin forces the
 *   site's use_motion off (`Customer.configure_motion`); nothing is pulled.
 * - AutoPatrol: patrols, not a pulled stream.
 * - Sites that don't read the connector settings file (AI Link, Sentinel
 *   Verifier, Clips): the value would never reach anything.
 */
export const MOTION_SETTING_HIDDEN_INTEGRATIONS = [
  "SMTP_per_camera",
  "speco_smtp",
  "autopatrol",
];

export const motionSettingApplies = (
  integrationTypeName: string | null | undefined
): boolean =>
  !!integrationTypeName &&
  !MOTION_SETTING_HIDDEN_INTEGRATIONS.includes(integrationTypeName) &&
  readsSettingsFile(integrationTypeName);

/**
 * Whether to offer the per-camera Motion setting (SUP-829). On a site that
 * doesn't use motion the control only confuses people, and "Use motion"
 * there would leave the camera waiting for motion events that nothing sends.
 * So it shows only when the site uses motion, the camera already does, or
 * the camera has its own setting that changes something (see
 * overrideMayMatter). Staff get the same rule.
 */
export const motionSettingVisible = ({
  integrationTypeName,
  siteUseMotion,
  cameraUsesMotion,
  overrideMatters,
}: {
  integrationTypeName: string | null | undefined;
  siteUseMotion: unknown;
  cameraUsesMotion?: boolean;
  overrideMatters?: boolean;
}): boolean =>
  motionSettingApplies(integrationTypeName) &&
  (siteUseMotion === true || !!cameraUsesMotion || !!overrideMatters);

/**
 * Whether a camera's own motion setting can change anything on a site that
 * doesn't use motion. "Use motion" already shows up as the camera using
 * motion, and "Always stream" is what the camera does anyway, so neither
 * keeps the setting on screen. Milestone is the exception: Inherit there
 * follows the camera's milestone_motion, which the camera list doesn't
 * carry, so an "Always stream" override may be what turns motion off.
 */
export const overrideMayMatter = ({
  integrationTypeName,
  override,
}: {
  integrationTypeName: string | null | undefined;
  override: unknown;
}): boolean =>
  integrationTypeName === "milestone" && typeof override === "boolean";

/**
 * What "Inherit" resolves to for this camera.
 *
 * With no override the backend's resolved `use_motion` IS the inherited value
 * (on Milestone that already folds in `milestone_motion`). With an override
 * set, the inherited value is the site's `use_motion`, except on Milestone
 * XProtect where it's the camera's `milestone_motion` (served by
 * `camera/{id}/extra_fields/`, not general_info). Null = unknown.
 */
export const inheritedMotionValue = ({
  override,
  effective,
  integrationTypeName,
  siteUseMotion,
  milestoneMotion,
}: {
  override: MotionOverride | undefined;
  effective: unknown;
  integrationTypeName: string | null | undefined;
  siteUseMotion: unknown;
  milestoneMotion?: unknown;
}): boolean | null => {
  if (override === null || override === undefined) {
    return typeof effective === "boolean" ? effective : null;
  }
  if (integrationTypeName === "milestone") {
    return typeof milestoneMotion === "boolean" ? milestoneMotion : null;
  }
  return typeof siteUseMotion === "boolean" ? siteUseMotion : null;
};

export interface CameraMotionState {
  override: MotionOverride;
  /** Resolved `use_motion` from general_info; null when the payload lacks it. */
  effective: boolean | null;
}

/** Reads the motion fields off a general_info payload; null if absent. */
export const cameraMotionFromGeneralInfo = (
  data: Record<string, any> | null | undefined
): CameraMotionState | null => {
  if (
    !data ||
    !Object.prototype.hasOwnProperty.call(data, MOTION_OVERRIDE_FIELD)
  ) {
    return null;
  }
  const override = data[MOTION_OVERRIDE_FIELD];
  return {
    override: typeof override === "boolean" ? override : null,
    effective: typeof data.use_motion === "boolean" ? data.use_motion : null,
  };
};

/**
 * The Edit Camera General Info tab is re-rendered from many places with
 * whichever cameraGeneralInfo snapshot that path captured, so a value saved by
 * the motion card would read stale after a tab switch. The page load and every
 * save write here, keyed by camera id; the card and the Axis/Mobotix
 * motion-off logic (motionOff.tsx) read from here first.
 */
interface CameraMotionStore {
  byCamera: Record<string, CameraMotionState>;
  setCameraMotion: (
    cameraId: string | number,
    state: CameraMotionState
  ) => void;
}

export const useCameraMotionStore = create<CameraMotionStore>((set) => ({
  byCamera: {},
  setCameraMotion: (cameraId, state) =>
    set((current) => ({
      byCamera: { ...current.byCamera, [String(cameraId)]: state },
    })),
}));

/** Record general_info's motion fields for the camera (no-op if absent). */
export const recordCameraMotionFromGeneralInfo = (
  data: Record<string, any> | null | undefined
) => {
  const state = cameraMotionFromGeneralInfo(data);
  if (state && data?.id != null) {
    useCameraMotionStore.getState().setCameraMotion(data.id, state);
  }
};

/** PATCH only the override; resolves to the camera's new motion state. */
export const patchCameraMotionOverride = async (
  cameraId: string | number,
  override: MotionOverride
): Promise<CameraMotionState> => {
  const response = await createApi("").patch(
    `/camera/${cameraId}/general_info/`,
    { [MOTION_OVERRIDE_FIELD]: override }
  );
  return (
    cameraMotionFromGeneralInfo(response?.data) ?? {
      override,
      effective: null,
    }
  );
};

/** Best-effort human reason from a failed request (400 field errors, detail). */
export const motionErrorMessage = (reason: any): string => {
  const data = reason?.response?.data;
  const fieldError = data?.[MOTION_OVERRIDE_FIELD];
  if (Array.isArray(fieldError) && fieldError.length > 0) {
    return String(fieldError[0]);
  }
  if (typeof fieldError === "string") return fieldError;
  if (typeof data?.detail === "string") return data.detail;
  const status = reason?.response?.status;
  if (status) return `Request failed (${status})`;
  return reason?.message ? String(reason.message) : "Request failed";
};

export interface BulkMotionCamera {
  id: string | number;
  camera_name?: string;
}

export interface BulkMotionResult {
  succeeded: BulkMotionCamera[];
  failed: { camera: BulkMotionCamera; error: string }[];
}

export const BULK_MOTION_CONCURRENCY = 4;

/**
 * PATCH the override on each camera, `concurrency` at a time (no bulk
 * endpoint). Never rejects: per-camera failures are collected so the caller
 * can show a summary. `onProgress(done, total)` fires after each camera.
 */
export const bulkSetMotionOverride = async (
  cameras: BulkMotionCamera[],
  override: MotionOverride,
  {
    concurrency = BULK_MOTION_CONCURRENCY,
    onProgress,
  }: {
    concurrency?: number;
    onProgress?: (done: number, total: number) => void;
  } = {}
): Promise<BulkMotionResult> => {
  const result: BulkMotionResult = { succeeded: [], failed: [] };
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < cameras.length) {
      const camera = cameras[next];
      next += 1;
      try {
        await patchCameraMotionOverride(camera.id, override);
        result.succeeded.push(camera);
      } catch (reason) {
        result.failed.push({ camera, error: motionErrorMessage(reason) });
      }
      done += 1;
      onProgress?.(done, cameras.length);
    }
  };
  const workers = Array.from(
    { length: Math.max(1, Math.min(concurrency, cameras.length)) },
    worker
  );
  await Promise.all(workers);
  return result;
};
